<template>
  <div
    v-if="visible"
    class="pwa-reload-progress"
    :class="{ 'pwa-reload-progress--done': done }"
    role="progressbar"
    aria-label="Reloading"
    data-testid="pwa-reload-progress"
  />
  <!-- The announcement is owned here, not by the bar above: `role="progressbar"` is a
       widget role, not a live region, so AT does not proactively speak the bar appearing
       or completing (WCAG 4.1.3). The bar is also `v-if`'d, and an aria-live element
       inserted at the same moment as its content is unreliably announced -- so this region
       is rendered unconditionally and only its TEXT changes, matching the sr-only
       live-region shape used on the agent pages (content-search.vue, join-override.vue). -->
  <span
    class="sr-only"
    role="status"
    aria-live="polite"
    data-testid="pwa-reload-live-region"
  >{{ announcement }}</span>
</template>

<script lang="ts" setup>
import { consumePwaReloadFlag } from '~/utils/pwa'

// Keep the bar on screen long enough to register even when the reloaded page is already cached
// and paints instantly.
const MIN_VISIBLE_MS = 500
const FADE_MS = 400

const visible = ref(false)
const done = ref(false)

// English literals rather than $t(): this feature already hardcodes its AT strings
// (`aria-label="Reloading"` above, `aria-label="Refresh"` in PwaControlBar), the agent
// pages' live regions do the same, and i18n/ is Lokalise-generated so a new key cannot
// ship from this repo.
const announcement = computed(() => {
  if (!visible.value) { return '' }
  return done.value ? 'Reload complete' : 'Reloading'
})

let fadeTimer: ReturnType<typeof setTimeout> | undefined
let hideTimer: ReturnType<typeof setTimeout> | undefined
let onLoaded: (() => void) | undefined

const finish = () => {
  done.value = true
  hideTimer = setTimeout(() => { visible.value = false }, FADE_MS)
}

onMounted(() => {
  // Only shows on the page that comes back from a control-bar Refresh, one time.
  if (!consumePwaReloadFlag()) { return }
  visible.value = true

  const mountedAt = Date.now()
  const complete = () => {
    const elapsed = Date.now() - mountedAt
    fadeTimer = setTimeout(finish, Math.max(0, MIN_VISIBLE_MS - elapsed))
  }

  if (document.readyState === 'complete') {
    complete()
  } else {
    onLoaded = complete
    window.addEventListener('load', onLoaded, { once: true })
  }
})

onBeforeUnmount(() => {
  if (fadeTimer) { clearTimeout(fadeTimer) }
  if (hideTimer) { clearTimeout(hideTimer) }
  if (onLoaded) { window.removeEventListener('load', onLoaded) }
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

.pwa-reload-progress {
  position: fixed;
  right: 0;
  left: 0;
  // Rest on top of the control bar so the bar and the home-indicator safe area never cover it.
  // --pwa-control-bar-offset (BaseLayout) is the bar's full height including its trimmed safe-area
  // padding; track it rather than restating the formula, which drifted 10px high on Nuxt2 when the
  // trim landed (AB#62620).
  bottom: var(--pwa-control-bar-offset, 64px);
  height: 3px;
  // One above the control bar (2147483646) so it is never hidden behind it.
  z-index: 2147483647;
  // `secondary` is #C93374 -- the same value the literal held, so no visual change.
  // 4.99:1 on white, clearing the 3:1 WCAG 1.4.11 non-text minimum for the bar.
  background: rgb(var(--v-theme-secondary));
  animation: pwa-reload-indeterminate 1.1s ease-in-out infinite;

  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    // Control bar is pinned to the top on desktop; sit the bar directly under it.
    top: var(--pwa-control-bar-height, 64px);
    bottom: auto;
  }
}

// A moving sliver of fill reads as "working" without pretending to measure real progress.
@keyframes pwa-reload-indeterminate {
  0% { clip-path: inset(0 100% 0 0); }
  50% { clip-path: inset(0 0 0 0); }
  100% { clip-path: inset(0 0 0 100%); }
}

// Once the page has loaded: fill the bar, then fade it out.
.pwa-reload-progress--done {
  animation: none;
  clip-path: inset(0 0 0 0);
  opacity: 0;
  transition: opacity 400ms ease; // keep in step with FADE_MS in the script
}

@media (prefers-reduced-motion: reduce) {
  .pwa-reload-progress {
    animation: none;
    clip-path: inset(0 0 0 0);
  }
}
</style>
